import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Table from '../../components/Table';
import TableSkeleton from './TableSkeleton';
import { useSelector } from 'react-redux';
import NoContent from '../../components/NoContent';
import { roleLabel } from '../../utils/roleLabel';
import { formatHumanDate } from '../../utils/gernalUtils';
import { moduleLabel, logTypeLabel, logStatusLabel, tagLabel, composedLogAction } from '../../utils/i18nLabels';
import { hasPermission } from '../../config/rbac';

const LogTable = ({ logData, logDataLoading, setLogDataLoading, setLogDetails }) => {
  const { t } = useTranslation();
  // Read role from BOTH known Redux shapes — matches PermissionGate. The
  // legacy `userLogin.userInfo.data.user.role` shape misses users who
  // hydrated via getLoggedinUserAction, which populates
  // `loggedinUser.userInfo.data.data.role`. Using both fixes the
  // "audit User/Role columns invisible" bug for systemAdmin/adjuster.
  const userRole = useSelector((state) => {
    const info    = state?.userLogin?.userInfo?.data;
    const loggedIn = state?.loggedinUser?.userInfo?.data;
    return loggedIn?.data?.role || info?.data?.role || info?.user?.role || null;
  });
  // Tenant timezone — must match the frame the /logs date-range filter
  // parses in (backend reads TenantConfig.locale.timezone). We MUST NOT
  // render timestamps before tenantConfig has hydrated, or the same audit
  // row displays first as UTC (fallback) then as Europe/Zurich (after
  // async fetch resolves) across reloads — that non-deterministic display
  // was mistaken for stored-value drift on ARGOS. Gate the whole render on
  // `tenantConfigReady` so the tz used for every row is the tenant's
  // configured value on the very first paint.
  const tenantConfigState = useSelector((state) => state?.tenantConfig) || {};
  const tenantConfigReady = !!(tenantConfigState.config && !tenantConfigState.loading);
  const tenantTz = tenantConfigState?.config?.locale?.timezone || 'UTC';

  // Gate columns via RBAC (audit.read) — one source of truth, no
  // hardcoded role lists to drift.
  const canReadUserFields = hasPermission(userRole, 'audit', 'read');
  const canReadHash       = hasPermission(userRole, 'integrity', 'read');
  const canReadClient     = userRole === 'admin';

  useEffect(() => {
    const timer = setTimeout(() => {
      setLogDataLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [logDataLoading, setLogDataLoading]);

  const convertTimestamp = timestamp => {
    const timestampInSeconds = parseInt(timestamp, 10);
    if (!isNaN(timestampInSeconds)) {
      return new Date(timestampInSeconds * 1000).toLocaleString();
    }
  };

  const convertEpochToDateTime = timestamp => {
    const timestampInSeconds = parseInt(timestamp, 10);

    if (!isNaN(timestampInSeconds)) {
      const date = new Date(timestampInSeconds * 1000);
      const year = date.getFullYear();
      const month = ('0' + (date.getMonth() + 1)).slice(-2);
      const day = ('0' + date.getDate()).slice(-2);
      const hours = ('0' + date.getHours()).slice(-2);
      const minutes = ('0' + date.getMinutes()).slice(-2);
      const seconds = ('0' + date.getSeconds()).slice(-2);
      const milliseconds = ('00' + date.getMilliseconds()).slice(-3);

      // Determine AM/PM
      const ampm = hours >= 12 ? 'pm' : 'am';
      const formattedHours = hours % 12 || 12; // Convert 0 to 12-hour format

      const formattedDateTime = `${year}-${month}-${day} ${formattedHours}:${minutes}:${seconds}.${milliseconds} ${ampm}`;
      return formattedDateTime;
    } else {
      return 'Invalid timestamp';
    }
  };

  return (
    <div>
      <Table
        thead={
          logData?.userInfo?.data && logData?.userInfo?.data && logData?.userInfo?.data.length < 0 ? (
            <TableSkeleton columns={9} rows={10} />
          ) : (
            <tr>
              <th style={{ background: 'none', marginLeft: '1rem', display: 'block' }}>{t('screens.logs.colTag')}</th>
              <th style={{ background: 'none' }}>{t('screens.logs.colType')}</th>
              <th style={{ background: 'none' }}>{t('screens.logs.colModule')}</th>
              <th style={{ background: 'none' }}>{t('screens.logs.colStatus')}</th>
              {canReadUserFields ? (
                <>
                  <th style={{ background: 'none' }}>{t('screens.logs.colUser')}</th>
                  <th style={{ background: 'none' }}>{t('screens.logs.colRole')}</th>
                </>
              ) : null}
              {canReadClient ? <th style={{ background: 'none' }}>{t('screens.logs.colClient')}</th> : null}
              <th style={{ background: 'none' }}>{t('screens.logs.colActionPerformedOn')}</th>
              <th style={{ background: 'none' }}>{t('screens.logs.colActionPerformedAt')}</th>
              <th style={{ background: 'none' }}>{t('screens.logs.colIp')}</th>
              {/* <th style={{ background: 'none' }}>Device</th> */}
              <th style={{ background: 'none' }}>{t('screens.logs.colPlatform')}</th>
              {canReadHash ? (
                <>
                  <th style={{ background: 'none' }}>{t('screens.logs.colEntryHash')}</th>
                  <th style={{ background: 'none' }}>{t('screens.logs.colPreviousHash')}</th>
                </>
              ) : null}
            </tr>
          )
        }
        tbody={
          // Wait for tenantConfig before rendering rows — see comment on
          // tenantConfigReady above. Rendering with the UTC fallback and
          // then re-rendering with Europe/Zurich (or America/Chicago for
          // MN) produced a visible timestamp shift on reload that the
          // 2026-07-13 tester recorded as data drift.
          !tenantConfigReady || logDataLoading || (logData && logData.loading) ? (
            <TableSkeleton columns={11} rows={10} />
          ) : logData && logData.userInfo && logData.userInfo.status === 'success' && logData.userInfo.data && logData.userInfo.data.result.length > 0 ? (
            logData.userInfo.data.result.map(log => (
              <tr key={log._id}>
                {/* actionTags are lowercase slugs ('users', 'read'); joining
                    them raw printed "users, read" to a French user. */}
                <td style={{ marginLeft: '1rem', display: 'block' }}>
                  {log.actionTags && log.actionTags.length > 0
                    ? log.actionTags.map(tag => tagLabel(t, tag)).join(', ')
                    : '-'}
                </td>
                {/* Both are raw API slugs. Capitalising them left the audit
                    table reading English ("Create", "Claims") for a French
                    user; `type` is an action or an entity, `module` is always a
                    module, so they resolve against different namespaces. */}
                <td>{log.type ? logTypeLabel(t, log.type) : '-'}</td>
                <td>{log.module ? moduleLabel(t, log.module) : '-'}</td>
                <td>
                  <span
                    style={{
                      padding: '3px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '600',
                      background: log.logType === 'success' ? '#dcfce7' : log.logType === 'fail' ? '#fee2e2' : '#f3f4f6',
                      color: log.logType === 'success' ? '#15803d' : log.logType === 'fail' ? '#991b1b' : '#6b7280',
                    }}
                  >
                    {log.logType ? logStatusLabel(t, log.logType) : t('screens.logs.statusNone')}
                  </span>
                </td>
                {canReadUserFields ? (
                  <>
                    <td>{log.userName || (log.user && log.user.name) || '-'}</td>
                    {/* Raw enum ('claimsManager') rendered verbatim — neither
                        translated nor tenant-aware. Same resolver as Team
                        Management and the role dropdowns. */}
                    <td>{log.userRole ? roleLabel(t, log.userRole) : '-'}</td>
                  </>
                ) : null}
                {canReadClient ? <td>{log.client ? (log.client.name ? log.client.name : '-') : '-'}</td> : null}
                {/* D-30 retest: fallback chain so this column is never
                    just "-". Legacy rows written by earlier logIt callers
                    that skipped description now show a compact
                    "<module> · <resource>" fingerprint. */}
                {/* The stored description/action is English prose written by
                    the backend and cannot be translated after the fact. Where
                    the row also carries structured method + resource, compose
                    the sentence in the reader's language instead; otherwise
                    keep the prose, which at least names the entity. */}
                <td>{composedLogAction(t, log)
                  || log.description || log.action
                  || [log.module, log.resource].filter(Boolean).join(' · ') || '-'}</td>
                <td>{log.timestamp ? formatHumanDate(log.timestamp, true, tenantTz) : '-'}</td>
                {/* D-30 retest: mask Docker bridge / IPv6-mapped internal
                    addresses (::ffff:172.x, 127.0.0.1, localhost) so the
                    audit UI shows "internal" instead of exposing container
                    plumbing that isn't the caller's real IP. */}
                <td>{(() => {
                  const ip = log.userIp || '';
                  if (!ip) return '-';
                  const stripped = ip.replace(/^::ffff:/, '');
                  if (stripped === '127.0.0.1' || stripped === '::1' || /^172\.(1[6-9]|2\d|3[01])\./.test(stripped) || /^10\./.test(stripped) || stripped === 'localhost') {
                    return t('screens.logs.internalIp', { defaultValue: 'internal' });
                  }
                  return stripped;
                })()}</td>
                <td>{log.loginInfo ? log.loginInfo : (log.userAgent ? String(log.userAgent).slice(0, 40) : '-')}</td>
                {/* <td>{log.logDevice ? log.logDevice.charAt(0).toUpperCase() + log.logDevice.slice(1).toLowerCase() : '-'}</td> */}
                {canReadHash ? (
                  <>
                    <td
                      style={{
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        title: log.entryHash,
                      }}
                    >
                      {log.entryHash ? log.entryHash.substring(0, 16) + '...' : '-'}
                    </td>
                    <td
                      style={{
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        title: log.previousHash,
                      }}
                    >
                      {log.previousHash ? log.previousHash.substring(0, 16) + '...' : '-'}
                    </td>
                  </>
                ) : null}
              </tr>
            ))
          ) : (
            <tr className='noContent_table_row'>
              <td colSpan={11}>
                <NoContent title1={t('screens.logs.noAuditLogs')} title2=' ' />
              </td>
            </tr>
          )
        }
      />
    </div>
  );
};

export default LogTable;
